import { useState, useEffect, FormEvent } from 'react'
import { motion } from 'framer-motion'
import { Lock } from 'lucide-react'

const CORRECT_PASSCODE = 'Almaveritas7'
const PASSCODE_KEY = 'wp_passcode_verified'

interface PasscodeGateProps {
  children: React.ReactNode
}

export function PasscodeGate({ children }: PasscodeGateProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [passcode, setPasscode] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check if user has already entered the correct passcode
    const verified = localStorage.getItem(PASSCODE_KEY)
    if (verified === 'true') {
      setIsAuthenticated(true)
    }
    setIsLoading(false)
  }, [])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (passcode === CORRECT_PASSCODE) {
      localStorage.setItem(PASSCODE_KEY, 'true')
      setIsAuthenticated(true)
      setError('')
    } else {
      setError('Incorrect passcode. Please try again.')
      setPasscode('')
    }
  }

  if (isLoading) {
    return null
  }

  if (isAuthenticated) {
    return <>{children}</>
  }

  return (
    <div className="w-full h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md mx-4"
      >
        <div className="flex flex-col items-center mb-6">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 text-center">
            Welcome to Wonderpath
          </h1>
          <p className="text-gray-600 text-center mt-2">
            Please enter the passcode to continue
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="password"
              value={passcode}
              onChange={(e) => {
                setPasscode(e.target.value)
                setError('')
              }}
              placeholder="Enter passcode"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500 transition-colors text-center text-lg"
              autoFocus
            />
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-red-500 text-sm text-center bg-red-50 py-2 px-4 rounded-lg"
            >
              {error}
            </motion.div>
          )}

          <button
            type="submit"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-200 shadow-lg hover:shadow-xl"
          >
            Enter
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Need access? Contact{' '}
            <a
              href="mailto:info@wonderpath.nyc"
              className="text-emerald-600 hover:text-emerald-700 underline"
            >
              info@wonderpath.nyc
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  )
}
